import { type CSSProperties, type KeyboardEvent, type MouseEvent, type PropsWithChildren, useCallback } from 'react';
import cn from 'clsx';
import { cva } from 'class-variance-authority';

import { useSearchResultCardContext } from './SearchResultCardProvider';
import { type DOMEventHandlers, type SearchResultCardContextValue } from './SearchResultCard.types';

import styles from './SearchResultCardArticleWrapper.module.css';

const articleVariants = cva(styles.article, {
  variants: {
    variant: {
      default: null,
      preview: styles.previewArticle,
    },
  },
});

const searchResultCardEventHandlerKeys = new Set([
  'onClickPropertyCardLink',
  'onContextMenuLinkOverlayActivate',
  'onContextMenuLinkOverlayDeactivate',
  'onPreferenceScoreClick',
  'onSearchResultCardContextMenu',
  'onSearchResultCardLinkClick',
  'onWatchlistClick',
]);

const isDOMEventHandlerProp = (key: string): key is keyof DOMEventHandlers =>
  key.startsWith('on') && !searchResultCardEventHandlerKeys.has(key);

const getEventHandlerProps = (cardProps: SearchResultCardContextValue) => {
  const eventHandlerProps: DOMEventHandlers = {};

  for (const key of Object.keys(cardProps)) {
    if (isDOMEventHandlerProp(key)) {
      Object.assign(eventHandlerProps, { [key]: cardProps[key] });
    }
  }

  return eventHandlerProps;
};

export const SearchResultCardArticleWrapper = ({ children, style }: PropsWithChildren<{ style?: CSSProperties }>) => {
  const context = useSearchResultCardContext();
  const {
    articleRef,
    className,
    isContextMenuLinkOverlayActive,
    listing,
    onSearchResultCardContextMenu,
    onSearchResultCardLinkClick,
    variant,
  } = context;
  const eventHandlerProps = getEventHandlerProps(context);

  const handleArticleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      eventHandlerProps.onClick?.(event);

      if (!event.defaultPrevented) {
        onSearchResultCardLinkClick(event);
      }
    },
    [eventHandlerProps, onSearchResultCardLinkClick],
  );

  const handleArticleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      eventHandlerProps.onKeyDown?.(event);

      if (!event.defaultPrevented) {
        onSearchResultCardLinkClick(event);
      }
    },
    [eventHandlerProps, onSearchResultCardLinkClick],
  );

  return (
    <article
      {...eventHandlerProps}
      className={cn(articleVariants({ variant }), className)}
      data-testid={`search-components_result-card_${listing.id}`}
      data-text-selection-disabled={isContextMenuLinkOverlayActive}
      onClick={handleArticleClick}
      onContextMenu={onSearchResultCardContextMenu}
      onKeyDown={handleArticleKeyDown}
      tabIndex={0}
      ref={articleRef}
      style={style}
    >
      {children}
    </article>
  );
};
